import { Navigate, createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { RedirectIfAuthenticated, RequireAdmin, RequireAuth } from '@/features/auth/guards';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { VerifyEmailPage } from '@/features/auth/VerifyEmailPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { AdminUsersPage } from '@/features/admin/AdminUsersPage';
import { AdminDecksPage } from '@/features/admin/AdminDecksPage';
import { AdminDeckEditorPage } from '@/features/admin/AdminDeckEditorPage';
import { AdminTrainingPage } from '@/features/admin/AdminTrainingPage';
import { AdminTrainingEditorPage } from '@/features/admin/AdminTrainingEditorPage';
import { TrainingListPage } from '@/features/training/TrainingListPage';
import { TrainingMaterialPage } from '@/features/training/TrainingMaterialPage';
import { NotFoundPage } from '@/routes/NotFoundPage';
import { RouteError } from '@/components/ui/RouteError';
import { SpreadsListPage } from '@/features/spreads/SpreadsListPage';
import { SpreadDetailPage } from '@/features/spreads/SpreadDetailPage';
import { SpreadSessionPage } from '@/features/spreads/SpreadSessionPage';
import { JoinSpreadSessionPage } from '@/features/spreads/JoinSpreadSessionPage';
import { GamesListPage } from '@/features/games/GamesListPage';
import { GameDetailPage } from '@/features/games/GameDetailPage';
import { GameSessionPage } from '@/features/games/GameSessionPage';
import { AdminRegistrationPage } from '@/features/admin/AdminRegistrationPage';
import { AdminSessionsPage } from '@/features/admin/AdminSessionsPage';
import { AdminGamesPage } from '@/features/admin/AdminGamesPage';
import { AdminSpreadsPage } from '@/features/admin/AdminSpreadsPage';
import { AdminOrdersPage } from '@/features/admin/AdminOrdersPage';
import { AdminProductsPage } from '@/features/admin/AdminProductsPage';
import { AdminSubscriptionsPage } from '@/features/admin/AdminSubscriptionsPage';
import { AdminPrivacyPage } from '@/features/admin/AdminPrivacyPage';
import { AdminAnalyticsPage } from '@/features/admin/AdminAnalyticsPage';
import { AdminSectionLayout } from '@/features/admin/AdminSectionLayout';
import { ADMIN_SECTIONS } from '@/features/admin/admin-sections';
import { UnsubscribePage } from '@/features/privacy/UnsubscribePage';
import { PaymentReturnPage } from '@/features/payments/PaymentReturnPage';
import { TariffsPage } from '@/features/payments/TariffsPage';
import { BuyPage } from '@/features/payments/BuyPage';

/**
 * Route map from §49.
 *
 * The guards here are a convenience: the server checks the session on every
 * request, so a hand-typed URL cannot reach data the user may not see (§64).
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    /*
      errorElement стоит у каждого корневого маршрута, а не один на всё
      приложение. React Router ищет ближайший вверх по дереву: так сбой на
      экране партии остаётся на экране партии. Ошибка порядка хуков у
      участника показывала белый экран именно потому, что перехватывать её
      было нечем.
    */
    errorElement: <RouteError />,
    children: [
      // Development reference — open without a session so the theme can be
      // reviewed without logging in.

      {
        element: <RequireAuth />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },

          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'profile', element: <ProfilePage /> },

          { path: 'spreads', element: <SpreadsListPage /> },
          { path: 'spreads/:spreadId', element: <SpreadDetailPage /> },
          { path: 'spreads/:spreadId/solo', element: <SpreadDetailPage /> },
          { path: 'spreads/:spreadId/session/:sessionId', element: <SpreadSessionPage /> },

          { path: 'games', element: <GamesListPage /> },
          { path: 'games/:gameId', element: <GameDetailPage /> },
          {
            path: 'games/:gameId/session/:sessionId',
            element: <GameSessionPage />,
            /* Read by AppLayout — a live board is watched, not read (§6). */
            handle: { wide: true },
          },

          { path: 'training', element: <TrainingListPage /> },
          { path: 'training/:materialId', element: <TrainingMaterialPage /> },

          /*
            Прямая оплата (docs/payments/ROBOKASSA_DIRECT.md): тарифы и возврат
            из Robokassa — SuccessURL и FailURL магазина.
          */
          { path: 'tariffs', element: <TariffsPage /> },
          // Платёжная ссылка товара из «Тарифы и оплата»: без входа — вход, затем оплата.
          { path: 'buy/:code', element: <BuyPage /> },
          { path: 'payment/success', element: <PaymentReturnPage outcome="success" /> },
          { path: 'payment/fail', element: <PaymentReturnPage outcome="fail" /> },

          {
            element: <RequireAdmin />,
            children: [
              { path: 'admin', element: <Navigate to={ADMIN_SECTIONS[0]!.pages[0]!.to} replace /> },
              /*
                Адреса страниц прежние — меняется только то, что вокруг них:
                вкладки раздела над страницей (features/admin/admin-sections.ts).
              */
              {
                element: <AdminSectionLayout />,
                children: [
                  { path: 'admin/users', element: <AdminUsersPage /> },
                  { path: 'admin/decks', element: <AdminDecksPage /> },
                  { path: 'admin/decks/:deckId', element: <AdminDeckEditorPage /> },
                  { path: 'admin/training', element: <AdminTrainingPage /> },
                  { path: 'admin/training/:materialId', element: <AdminTrainingEditorPage /> },
                  { path: 'admin/games', element: <AdminGamesPage /> },
                  { path: 'admin/spreads', element: <AdminSpreadsPage /> },
                  { path: 'admin/orders', element: <AdminOrdersPage /> },
                  { path: 'admin/products', element: <AdminProductsPage /> },
                  { path: 'admin/subscriptions', element: <AdminSubscriptionsPage /> },
                  { path: 'admin/sessions', element: <AdminSessionsPage /> },
                  { path: 'admin/registration', element: <AdminRegistrationPage /> },
                  { path: 'admin/privacy', element: <AdminPrivacyPage /> },
                  { path: 'admin/analytics', element: <AdminAnalyticsPage /> },
                ],
              },
            ],
          },
        ],
      },

      { path: '*', element: <NotFoundPage /> },
    ],
  },

  {
    element: <RedirectIfAuthenticated />,
    errorElement: <RouteError />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },

  {
    /*
      Смена пароля по ссылке из письма — вне проверки «уже вошёл».

      Ссылкой пользуются в том числе тогда, когда в аккаунт попал посторонний, и
      человек открывает письмо на устройстве, где сам ещё залогинен. Отправлять
      его при этом на главную значило бы не дать сменить пароль ровно в тот
      момент, когда это нужнее всего.
    */
    path: '/reset-password',
    element: <ResetPasswordPage />,
    errorElement: <RouteError />,
  },

  {
    /*
      Подтверждение почты — тоже вне проверки «уже вошёл»: письмо открывают на
      том устройстве, где почта, а не на том, где заходили.
    */
    path: '/verify-email',
    element: <VerifyEmailPage />,
    errorElement: <RouteError />,
  },

  {
    /*
      Отписка по ссылке из письма — вне проверки «уже вошёл» и без входа:
      письмо открывают где угодно (docs/PRIVACY.md).
    */
    path: '/unsubscribe',
    element: <UnsubscribePage />,
    errorElement: <RouteError />,
  },

  {
    /*
      Экран гостя — единственный, куда человек попадает по ссылке, ничего не
      зная о платформе. Белый экран здесь означает, что участник просто не
      придёт на сессию и не поймёт почему.
    */
    path: '/join/:token',
    element: <JoinSpreadSessionPage />,
    errorElement: <RouteError />,
  },
]);
